import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Client for Dr. Future, the Global Health AI health-information companion
 * (Api::DrFutureController, docs/globalhealth/DR_FUTURE_API_CONTRACT.md).
 *
 * The backend stores every conversation and calls the provider in a background
 * job; posting a question returns at once with a `pending` answer that the page
 * polls until it turns into answer | clarify | handoff | error | failed.
 */

export type DrFutureAssistantStatus = 'pending' | 'answer' | 'clarify' | 'handoff' | 'error' | 'failed';

/** Why an assistant turn failed (status `failed`). Mirrors DrFutureMessage::ERROR_CODES. */
export type DrFutureErrorCode =
  | 'unreachable' | 'timeout' | 'rate_limited' | 'upstream_error' | 'stale'
  | 'bad_request' | 'unauthorized' | 'not_found' | 'bad_response' | 'protocol_mismatch' | 'disabled';

export interface DrFutureSource {
  id:           string;
  title:        string;
  organization: string | null;
  url:          string | null;
  jurisdiction: string | null;
  source_type:  string | null;
  /** Section label and excerpt — planned on the provider side, absent today. The entry
   *  renders them the day they arrive (template CHANGES.md §2); do not remove. */
  section?:     string | null;
  snippet?:     string | null;
}

export type DrFutureFeedbackRating = 'up' | 'down';

/**
 * The answer mode of a Dr. Future conversation, fixed when the thread is created and
 * never mixed afterwards. `alternative` (holistic) is only accepted by the backend once
 * `alternative_mode_enabled` is true; until then it answers 503 `mode_not_available`.
 */
export type DrFutureMode = 'evidence' | 'alternative';

/**
 * The member's rating of one assistant turn — our copy. `sync_status` says whether
 * it reached the provider; the member only ever sees a quiet note when it failed.
 */
export interface DrFutureFeedback {
  rating:      DrFutureFeedbackRating;
  comment:     string | null;
  given_at:    string;
  updated_at:  string;
  sync_status: 'pending' | 'synced' | 'failed' | null;
}

export interface DrFutureMessage {
  id:          number;
  role:        'user' | 'assistant';
  status:      'sent' | DrFutureAssistantStatus;
  /**
   * Plain text on both sides: the question, or the provider's answer. Newlines are the
   * answer's only structure (plus `- ` bullets and `[n]` citations) — parse it into
   * text segments and interpolate them, never render it as HTML.
   */
  content:     string | null;
  sources:     DrFutureSource[];
  created_at:  string;
  updated_at:  string;
  request_id?:             string;
  in_reply_to_request_id?: string;
  error_code?:             DrFutureErrorCode | null;
  retryable?:              boolean;
  retry_allowed?:          boolean;
  /** Only for status `handoff`: the human Health Concierge with the question pre-filled. */
  handoff_url?:            string;
  /** answer | clarify | handoff turns may be rated. */
  feedback_allowed?:       boolean;
  feedback?:               DrFutureFeedback | null;
}

export interface DrFutureThread {
  id:              number;
  /** Which assistant the conversation belongs to (older backends omit it = dr_future). */
  assistant?:      string;
  title:           string;
  /** Answer mode, fixed per conversation (older backends omit it = evidence). */
  mode?:           DrFutureMode;
  status:          'open' | 'archived';
  archived:        boolean;
  created_at:      string;
  last_message_at: string | null;
  pending:         boolean;
  unread:          boolean;
  last_message:    { role: string; status: string; preview: string } | null;
}

export interface DrFutureAskState {
  can_ask:        boolean;
  blocked_reason: string | null;
}

export type DrFutureDnaReportStatus = 'none' | 'pending' | 'ready';

/** One assistant as `GET /dr_future/assistants` reports it for the viewer. */
export interface DrFutureAssistantState {
  key:            string;
  /** The provider URL is configured. Not connected = reading works, asking is refused. */
  connected:      boolean;
  locked:         boolean;
  locked_reason:  'dna_report_required' | 'not_connected' | null;
  can_ask:        boolean;
  blocked_reason: string | null;
}

export interface DrFutureAssistantsResponse {
  dna_report_status: DrFutureDnaReportStatus;
  assistants:        DrFutureAssistantState[];
  /** The backend accepts `mode: 'alternative'` (older backends omit it = false). */
  alternative_mode_enabled?: boolean;
}

export interface DrFutureThreadList extends DrFutureAskState {
  threads: DrFutureThread[];
  /** Same flag as on `/assistants`, so a page that skipped that call still knows it. */
  alternative_mode_enabled?: boolean;
}

export interface DrFutureThreadDetail extends DrFutureAskState {
  thread:   DrFutureThread;
  messages: DrFutureMessage[];
}

@Injectable({ providedIn: 'root' })
export class DrFutureService {
  private http = inject(HttpClient);

  /** Lock / connection / ask state of every assistant — the authority for the page states. */
  assistants(): Observable<DrFutureAssistantsResponse> {
    return this.http.get<DrFutureAssistantsResponse>('/dr_future/assistants');
  }

  listThreads(assistant: string, archived = false): Observable<DrFutureThreadList> {
    const params: Record<string, string | number> = { assistant };
    if (archived) params['archived'] = 1;
    return this.http.get<DrFutureThreadList>('/dr_future/threads', { params });
  }

  /**
   * A new conversation with an assistant, optionally with its first question (one request).
   * `mode` is only sent when it is `alternative`, so an evidence conversation posts exactly
   * the body an older backend already knows.
   */
  createThread(assistant: string, question: string | null, lang: string, mode: DrFutureMode = 'evidence'): Observable<DrFutureThreadDetail> {
    const body: Record<string, string> = question ? { assistant, question, lang } : { assistant, lang };
    if (mode === 'alternative') body['mode'] = mode;
    return this.http.post<DrFutureThreadDetail>('/dr_future/threads', body);
  }

  getThread(id: number, lang: string): Observable<DrFutureThreadDetail> {
    return this.http.get<DrFutureThreadDetail>(`/dr_future/threads/${id}`, { params: { lang } });
  }

  ask(threadId: number, question: string, lang: string): Observable<{ thread: DrFutureThread; messages: DrFutureMessage[] }> {
    return this.http.post<{ thread: DrFutureThread; messages: DrFutureMessage[] }>(
      `/dr_future/threads/${threadId}/messages`, { question, lang });
  }

  /** Re-sends a failed turn with the SAME request_id (the backend keeps it). */
  retry(messageId: number): Observable<{ thread: DrFutureThread; message: DrFutureMessage }> {
    return this.http.post<{ thread: DrFutureThread; message: DrFutureMessage }>(`/dr_future/messages/${messageId}/retry`, {});
  }

  /** 👍/👎 + optional comment. Re-posting with another rating or comment updates it. */
  feedback(messageId: number, rating: DrFutureFeedbackRating, comment: string | null): Observable<{ message: DrFutureMessage }> {
    return this.http.post<{ message: DrFutureMessage }>(
      `/dr_future/messages/${messageId}/feedback`, comment ? { rating, comment } : { rating });
  }

  update(threadId: number, changes: { title?: string; archived?: boolean }): Observable<{ thread: DrFutureThread }> {
    return this.http.patch<{ thread: DrFutureThread }>(`/dr_future/threads/${threadId}`, changes);
  }
}
